function isSafari() {
  const ua = navigator.userAgent
  return ua.match('Safari') !== null && ua.match('Chrome') === null
}

function isFirefox() {
  const ua = navigator.userAgent
  return ua.match('Firefox') !== null
}

const checkVideoFrameCallback = () =>
  typeof window !== 'undefined' &&
  typeof HTMLVideoElement.prototype.requestVideoFrameCallback === 'function'

export interface IVideoStallInfo {
  pts: number
  stall: {
    stallCount500ms: number
    stallDuration500ms: number
    stallRate500ms: number
    stallCount100ms?: number
    stallDuration100ms?: number
    stallRate100ms?: number
  }
  pauseDuration: number
}

export interface GetStallInfoParams {
  // rtc_media_statistics 上报间隔
  interval: number
  // 解码帧率
  frameRateDecoded?: number
  // 接收帧率
  frameRateReceived?: number
  // 接收码率
  bitrate?: number
}

class VideoStallObserver {
  private _removeHandler?: () => void

  private _remotePlayPauseHandler?: () => void

  private _prePts?: number

  private _stallList?: { playTime: number; timeUpdateInterval: number }[]

  private _videoInWaiting100ms = false

  private _videoInWaiting500ms = false

  private _isPaused?: boolean

  private _pauseStart = 0

  private _pauseDuration = 0

  private _requestVideoFrameCallbackTimer?: number

  private _player?: HTMLVideoElement

  private _stallTimeThreshold = {
    stall500ms: isSafari() || isFirefox() ? 550 : 500,
    stall100ms: 100
  }

  private _support100msStall = checkVideoFrameCallback()

  public start(player: HTMLVideoElement) {
    this._player = player
    if (this._support100msStall) {
      if (player) {
        this._requestVideoFrameCallbackTimer = player.requestVideoFrameCallback(
          this._onVideoRefresh.bind(this)
        )
        this._removeHandler = () => {
          if (this._requestVideoFrameCallbackTimer) {
            player.cancelVideoFrameCallback(this._requestVideoFrameCallbackTimer)
          }
        }
      }
    } else {
      const handle = () => this._onVideoTimeupdate(player)
      player.addEventListener('timeupdate', handle)
      this._removeHandler = () => player.removeEventListener('timeupdate', handle)
    }
    const handlePause = () => this._onVideoPause()
    player.addEventListener('pause', handlePause)
    const handlePlay = () => this._onVideoPlay()
    player.addEventListener('play', handlePlay)
    this._remotePlayPauseHandler = () => {
      player.removeEventListener('pause', handlePause)
      player.removeEventListener('play', handlePlay)
    }

    document.addEventListener('visibilitychange', this._handleVisibilityChange)
    window.addEventListener('pagehide', this._framePause)
  }

  public stop() {
    delete this._player
    if (this._removeHandler) {
      this._removeHandler()
    }
    delete this._removeHandler
    if (this._remotePlayPauseHandler) {
      this._remotePlayPauseHandler()
    }
    delete this._remotePlayPauseHandler
    delete this._stallList
    delete this._prePts
    this._videoInWaiting100ms = false
    this._videoInWaiting500ms = false
    document.removeEventListener('visibilitychange', this._handleVisibilityChange)
    window.removeEventListener('pagehide', this._framePause)
  }

  public destroy() {
    this.stop()
  }

  public getStallInfo({
    interval,
    frameRateReceived,
    frameRateDecoded,
    bitrate
  }: GetStallInfoParams): IVideoStallInfo {
    const stallInfo: IVideoStallInfo = {
      pts: 0,
      stall: {
        stallCount500ms: 0,
        stallDuration500ms: 0,
        stallRate500ms: 0,
        stallCount100ms: 0,
        stallDuration100ms: 0,
        stallRate100ms: 0
      },
      pauseDuration: this._getPauseDuration()
    }
    // 播放器在后台或者暂停播放时不计算卡顿率
    if (this._stallList && document.visibilityState === 'visible' && !this._isPaused) {
      const poorNetworkInSafari =
        (isSafari() || isFirefox()) &&
        (bitrate === 0 ||
          (frameRateDecoded || Infinity) <= 1 ||
          (frameRateReceived || Infinity) <= 1)

      if (poorNetworkInSafari || this._stallList.length === 0) {
        stallInfo.stall.stallDuration500ms = interval
        stallInfo.stall.stallCount500ms = this._videoInWaiting500ms ? 0 : 1
        stallInfo.stall.stallRate500ms = 1
        this._videoInWaiting500ms = true
        if (this._support100msStall) {
          stallInfo.stall.stallDuration100ms = interval
          stallInfo.stall.stallCount100ms = this._videoInWaiting100ms ? 0 : 1
          stallInfo.stall.stallRate100ms = 1
          this._videoInWaiting100ms = true
        }
      } else {
        this._stallList.forEach((frameRecord, idx) => {
          const { timeUpdateInterval, playTime } = frameRecord
          let stallDuration = timeUpdateInterval
          if (idx === 0 && (this._videoInWaiting100ms || this._videoInWaiting500ms)) {
            stallDuration = Math.round(timeUpdateInterval % interval)
          }
          if (timeUpdateInterval > this._stallTimeThreshold.stall500ms) {
            stallInfo.stall.stallDuration500ms += stallDuration
            if (!this._videoInWaiting500ms) {
              stallInfo.stall.stallCount500ms++
            }
            this._videoInWaiting500ms = true
          } else {
            this._videoInWaiting500ms = false
          }
          if (this._support100msStall) {
            if (timeUpdateInterval > this._stallTimeThreshold.stall100ms) {
              stallInfo.stall.stallDuration100ms! += stallDuration
              if (!this._videoInWaiting100ms) {
                stallInfo.stall.stallCount100ms!++
              }
              this._videoInWaiting100ms = true
            } else {
              this._videoInWaiting100ms = false
            }
          }
          stallInfo.pts = playTime
        })
      }
      const { stallDuration500ms, stallDuration100ms } = stallInfo.stall

      stallInfo.stall.stallRate500ms = Math.min(
        Number((stallDuration500ms / interval).toFixed(4)),
        1
      )

      if (this._support100msStall && stallDuration100ms) {
        stallInfo.stall.stallRate100ms = Math.min(
          Number((stallDuration100ms / interval).toFixed(4)),
          1
        )
      } else {
        delete stallInfo.stall.stallRate100ms
        delete stallInfo.stall.stallCount100ms
        delete stallInfo.stall.stallDuration100ms
      }
      this._stallList = []
    }
    return stallInfo
  }

  private _getPauseDuration() {
    let pause_duration = this._pauseDuration
    this._pauseDuration = 0
    if (this._isPaused) {
      const now = Date.now()
      const diff = now - (this._pauseStart || 0)
      if (diff > 500) {
        this._pauseStart = now
        pause_duration += diff
      }
    }
    return pause_duration
  }

  private _onVideoPause() {
    this._isPaused = true
    this._pauseStart = Date.now()
    this._framePause()
  }

  private _onVideoPlay() {
    this._isPaused = false
    const diff = Date.now() - this._pauseStart
    if (diff > 500) {
      this._pauseDuration += diff
    }
  }

  private _onVideoTimeupdate(video: HTMLVideoElement) {
    if (!video || video.currentTime === 0) {
      return
    }
    if (this._prePts === undefined) {
      this._prePts = video.currentTime
      return
    }
    if (!this._stallList) {
      this._stallList = []
    }
    if (video.currentTime > this._prePts) {
      const delta = video.currentTime - this._prePts
      this._stallList.push({
        playTime: video.currentTime,
        timeUpdateInterval: Math.round(delta * 1000)
      })
    }
    this._prePts = video.currentTime
  }

  private _onVideoRefresh(_: number, data: VideoFrameCallbackMetadata) {
    if (!this._stallList) {
      this._stallList = []
    }
    if (this._prePts) {
      const delta = data.presentationTime - this._prePts

      this._stallList.push({
        playTime: data.presentationTime,
        timeUpdateInterval: Math.round(delta)
      })
    }
    this._prePts = data.presentationTime
    if (this._player) {
      this._player.requestVideoFrameCallback(this._onVideoRefresh.bind(this))
    }
  }

  private _handleVisibilityChange = () => {
    if (document.visibilityState === 'hidden') {
      this._framePause()
    }
  }

  private _framePause = () => {
    if (this._prePts && this._stallList) {
      // 清空当前存量数据
      const current = performance.now()
      const delta = current - this._prePts
      this._stallList.push({ playTime: current, timeUpdateInterval: Math.round(delta) })

      this._prePts = undefined
    }
  }
}

export default VideoStallObserver
